import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global, useMenuStore } from "@shared/libs/Global";
import { getCurrentTime, getInitFormData } from "@shared/utils/CommonUtils";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { Divider, IconButton, List, Menu, MenuItem } from "@mui/material";
import "./SN_SFA_ISSUE_REP_005.css";
import SN_SFA_ISSUE_REP_005_POP_001 from "@menu/SFA/SN_SFA_ISSUE_REP_005/SN_SFA_ISSUE_REP_005_POP_001";
import { showIssueModal } from "@widgets/IssueModal";
import SnEditor from "@shared/components/SnEditor";
import MoreIcon from "@mui/icons-material/MoreVert";
import { getProgress, getReply } from "@shared/utils/IssueUtils";
import { showDialog } from "@widgets/CommonDialog";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnResizable from "@shared/components/SnResizable";
import SN_SFA_ISSUE_REP_005_POP_002 from "@menu/SFA/SN_SFA_ISSUE_REP_005/SN_SFA_ISSUE_REP_005_POP_002";
import SN_SFA_ISSUE_REG_005_POP_002 from "@menu/SFA/SN_SFA_ISSUE_REG_005/SN_SFA_ISSUE_REG_005_POP_002";
import { sendIssueSnapshotTelegramByParams } from "../issueTelegram";
import { UseApiService } from "@shared/hooks/useApiService";

interface Props {
  Menuprops: menuInfo;
}

const STATUS_MAP: Record<string, string> = {
  "0": "TEMP",
  "1": "OPEN",
  "2": "ASSIGN",
  "3": "SOLVING",
  "4": "DONE",
  "5": "CLOSE",
  "9": "CANCEL",
  T: "TEST",
};

/**
 * 메뉴 : ISSUE LIST
 */
const SN_SFA_ISSUE_REP_005 = forwardRef(({ Menuprops }: Props, ref) => {
  const searchRef = useRef<any>(null);
  const { userInfo } = global();
  const { menuItems } = useMenuStore();
  const uniqueId = useId();

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [issueList, setIssueList] = useState<any>([]);
  const [detailTitle, setDetailTitle] = useState<string>("");
  const [toolTipTitle, setToolTipTitle] = useState<string>("");
  const [detailBody, setDetailBody] = useState<any>(null);
  const [replyList, setReplyList] = useState<any[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);
  const [isExpandedHeader, setIsExpandedHeader] = useState(false);

  //#region editor 관련
  const [editorBody, setEditorBody] = useState<string>("");
  //#endregion
  const { apiPost } = UseApiService();

  //#region menu 관련
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_SFA_ISSUE_REP_005";
  const defaultSize = {
    panel1width: "50%",
    panel2width: "48.5%",
    panel3height: "200px",
  };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  const zoomRef = useRef<any>(null);
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
      } catch (error) {}
    };
    fetchform();
  }, [userInfo]);

  useEffect(() => {
    if (formData) {
      onSearch(true);
    }
  }, [formData]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
    onInsert,
    onDelete,
  }));

  const onSearch = async (isInit?: boolean) => {
    try {
      // cd_emp와 nm_emp_e 조회
      let form = formData;
      if (!isInit) {
        form = searchRef.current.getFormdata();
      }
      const searchEmp = form.search_emp?.trim() || "";

      // 1. 한글 포함 여부 체크
      const isKorean = /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(searchEmp);

      // 2. 검색 조건 설정
      const empSearchParam = {
        cd_firm: form.cd_firm,
        ...(isKorean ? { nm_emp: searchEmp } : { nm_emp_e: searchEmp }),
      };

      // 3. 조회 요청
      const params1 = {
        id: "sn_sfa_issue_rep_005_03",
        params: empSearchParam,
      };
      const empData: any = await fnGetDataSet(params1);

      const cd_emp =
        empData?.[0]?.map((item: any) => item.cd_emp).join(",") || searchEmp;
      const nm_emp =
        empData?.[0]?.map((item: any) => item.nm_emp).join(",") || searchEmp;

      const params2 = {
        id: "sn_sfa_issue_rep_005_01",
        params: { ...form, cd_emp: cd_emp, nm_emp: nm_emp },
      };
      const data: any = await fnGetDataSet(params2);

      setIssueList([]);
      setPage(null);
      if (data[0] && data[0].length > 0) {
        // 왼쪽에 list 그려주기
        setIssueList(data[0]);

        // 첫번째 row focus
        setPage(data[0][0]);
        setSelectedIssue(data[0][0]);
        setEditorBody("");
      }
    } catch (error: any) {}
  };

  const handleDoubleClick = async (issue: any) => {
    const cd_menu = "SN_SFA_ISSUE_REG_005";
    const menuList = menuItems;
    if (Menuprops) {
      const filePath =
        `./${Menuprops.cd_module}/${cd_menu}/${cd_menu}`.toUpperCase();
      const drillDown = menuList
        ? menuList.find((item: any) => item?.cd_menu === cd_menu)
        : undefined;

      if (drillDown) {
        (window as any).onActivate(
          drillDown.cd_menu, // 메뉴 키
          drillDown.nm_menu, // 메뉴 제목
          drillDown.cd_module, // 모듈
          filePath, // 파일 경로
          drillDown, // 데이터
          drillDown.nm_path, // 파일경로 한글화
          issue,
          "fg_drilldown.Search"
        );
      }
    }
  };

  const setPage = async (issueData: any) => {
    const no_issue = issueData?.no_issue || "";
    const dt_issue = issueData?.dt_issue || "";
    const dt_due = issueData?.dt_due || "";
    const nm_emp = issueData?.nm_emp || "";
    const dc_title = issueData?.dc_title?.trim() || "";
    const nm_cust = issueData?.nm_cust || "";
    const nm_fg_item = issueData?.nm_fg_item || "";
    const tp_issue1 = issueData?.nm_tp_issue1 || "";
    const tp_issue2 = issueData?.nm_tp_issue2 || "";
    const fg_priority = issueData?.nm_fg_priority || "";
    const no_issue_rel = issueData?.no_issue_rel || "";
    const nm_emp_admin = issueData?.nm_emp_admin || "";
    const nm_recipient = issueData?.nm_recipient || "";
    const cd_pjt = issueData?.cd_pjt || "";
    const nm_reference = issueData?.nm_reference || "";
    const nm_emp_start = issueData?.nm_emp_start || "";
    const tm_start = issueData?.tm_start || "";
    const nm_emp_finish = issueData?.nm_emp_finish || "";
    const tm_finish = issueData?.tm_finish || "";
    const dt_assign = issueData?.dt_assign || "";
    const dt_close = issueData?.dt_close || "";
    const dt_cancel = issueData?.dt_cancel || "";
    const st_issue = STATUS_MAP[issueData?.st_issue] || "";
    const dc_tag = issueData?.dc_tag || "";
    const qt_md = issueData?.qt_md || "";
    const fg_schedule = issueData?.fg_schedule || "";
    const cd_menu = issueData?.cd_menu || "";
    const nm_emp_design = issueData?.nm_emp_design || "";
    const nm_emp_dev = issueData?.nm_emp_dev || "";

    const tooltipTitle = [
      `이슈번호: ${no_issue}`,
      `등록일: ${dt_issue}`,
      `마감일: ${dt_due}`,
      `등록자: ${nm_emp}`,
      `제목: ${dc_title}`,
      `거래처: ${nm_cust}`,
      `제품구분: ${nm_fg_item}`,
      `유형1: ${tp_issue1}`,
      `유형2: ${tp_issue2}`,
      `우선순위: ${fg_priority}`,
      `관련이슈: ${no_issue_rel}`,
      `관리자: ${nm_emp_admin}`,
      `담당자: ${nm_recipient}`,
      `참조자: ${nm_reference}`,
      `프로젝트: ${cd_pjt}`,
      `시작정보: ${nm_emp_start} | ${tm_start}`,
      `완료정보: ${nm_emp_finish} | ${tm_finish}`,
      `배정일: ${dt_assign}`,
      `마감일: ${dt_close}`,
      `취소일: ${dt_cancel}`,
      `상태: ${st_issue}`,
      `태그: ${dc_tag}`,
      `예정공수: ${qt_md}`,
      `일정등록: ${fg_schedule}`,
      `메뉴: ${cd_menu}`,
      `메뉴디자인: ${nm_emp_design}`,
      `메뉴개발: ${nm_emp_dev}`,
    ].join("\n");
    setToolTipTitle(tooltipTitle);

    const title = `
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; margin-bottom: 4px;">
        <div>${no_issue}</div>
        <div>${dt_issue}</div>
        <div>${dt_due}</div>
        <div>${nm_emp}</div>
      </div>
      <div style="display: grid; grid-template-columns: 2fr 0fr 1fr; margin-bottom: 4px;">
        <div style="grid-column: span 2;">${dc_title}</div>
        <div>${STATUS_MAP[issueData?.st_issue] || ""}</div>
      </div>
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; margin-bottom: 4px;">
        <div>${nm_fg_item}</div>
        <div>${issueData?.nm_tp_issue1 ?? ""}</div>
        <div>${issueData?.nm_tp_issue2 ?? ""}</div>
      </div>
      <div style="display: grid; grid-template-columns: 1fr 1fr;">
        <div>${nm_recipient}</div>
        <div>${nm_reference}</div>
      </div>
    `;

    if (!no_issue && !title) {
      setDetailTitle("");
      setDetailBody(null);
      setReplyList([]);
      return;
    }

    try {
      const temp = {
        cd_firm: userInfo.cd_firm,
        no_issue: no_issue,
      };
      const params = {
        id: "sn_sfa_issue_rep_005_02",
        params: temp,
      };
      const data: any = await fnGetDataSet(params);

      if (data[0] && data[0].length > 0) {
        // 오른쪽 영역에 setPage
        const body = data[0][0];
        const reply = data[1];

        setDetailTitle(title || "");
        setDetailBody(body);
        setReplyList(reply[0] === null ? [] : reply);
      }
    } catch (error: any) {}
  };

  const onInsert = async () => {
    // popup 띄우기
    const component = <SN_SFA_ISSUE_REP_005_POP_001 Menuprops={Menuprops} />;
    const result = await showIssueModal({
      title: "이슈생성 도움창",
      children: component,
      width: "70%",
    });
    if (result) {
      await onSearch();
    }
  };

  const onDelete = async () => {
    if (!selectedIssue) return;

    const saveData = {
      sfa_issue: {
        delete: [selectedIssue],
      },
    };

    try {
      await fnSave("SN_SFA_ISSUE_REP_005_btnNewIssue", saveData);
      await onSearch(true);
    } catch (err) {}
  };

  const onSave = async (changedData?: any) => {
    if (!selectedIssue) return false;

    const data = changedData || selectedIssue;
    data.dt_due = data.dt_due.replaceAll("/", "");

    const updateData: any = {
      cd_firm: userInfo.cd_firm,
      no_issue: data.no_issue,
      cd_user_amd: userInfo.cd_user,
      nm_user_amd: userInfo.nm_user,
      tm_amd: data.tm_amd,
      st_issue: data.st_issue,
    };

    const js_reply = getReply(data, replyList || [], editorBody);
    const js_progress = getProgress(data);

    if (js_reply) updateData.js_reply = js_reply;
    if (js_progress) updateData.js_progress = js_progress;

    const saveData = {
      sfa_issue: {
        update: [updateData],
      },
    };

    const result = await callApi("SN_SFA_ISSUE_REP_005_btnNewIssue", saveData);
    if (result.status) {
      updateData.dc_title = selectedIssue.dc_title;
      await sendMessage(updateData);
      const params = {
        id: "sn_sfa_issue_rep_005_04",
        params: {
          no_issue: selectedIssue.no_issue,
        },
      };
      const data: any = await fnGetDataSet(params);
      if (data[0] && data[0].length > 0) {
        setIssueList((prevIssueList: any) => {
          const newIssues = data[0];

          // 기존 리스트를 복사
          let updatedList = [...prevIssueList];
          newIssues.forEach((newIssue: any) => {
            const index = updatedList.findIndex(
              (issue) => issue.no_issue === newIssue.no_issue
            );
            if (index !== -1) {
              // 같은 no_issue가 있으면 덮어쓰기
              updatedList[index] = newIssue;
            }
          });

          return updatedList;
        });

        setPage(data[0][0]);
        setSelectedIssue(data[0][0]);
      }
      setEditorBody(""); // 에디터 초기화
      return true;
    }
    setEditorBody("");
    return false;
  };

  const saveIssueStatusAndAcceptDeployQueue = async (
    changedData: any,
    requestedAction: "CONF",
    previousStIssue: string
  ) => {
    if (!selectedIssue) return false;

    const data = changedData || selectedIssue;
    const updateData: any = {
      cd_firm: userInfo.cd_firm,
      no_issue: data.no_issue,
      cd_user_amd: userInfo.cd_user,
      nm_user_amd: userInfo.nm_user,
      tm_amd: data.tm_amd,
      st_issue: data.st_issue,
    };

    const js_reply = getReply(data, replyList || [], editorBody);
    const js_progress = getProgress(data);

    if (js_reply) updateData.js_reply = js_reply;
    if (js_progress) updateData.js_progress = js_progress;

    const response = await apiPost(
      "/SN_SFA_ISSUE_REG_005_SAVE_STATUS_DEPLOY_QUEUE",
      {
        requested_action: requestedAction,
        previous_st_issue: previousStIssue,
        cd_user: userInfo.cd_user,
        sfa_issue: {
          update: [updateData],
        },
      }
    );

    if (response.status === "S") {
      updateData.dc_title = selectedIssue.dc_title;
      await sendMessage(updateData);
      await refreshSelectedIssue(selectedIssue.no_issue);
      setEditorBody("");
      await showDialog("success", "저장되었습니다");
      return true;
    }

    setEditorBody("");
    return false;
  };

  const refreshSelectedIssue = async (noIssue: string) => {
    const params = {
      id: "sn_sfa_issue_rep_005_04",
      params: {
        no_issue: noIssue,
      },
    };
    const data: any = await fnGetDataSet(params);
    if (data[0] && data[0].length > 0) {
      setIssueList((prevIssueList: any) => {
        const newIssues = data[0];
        const updatedList = [...prevIssueList];
        newIssues.forEach((newIssue: any) => {
          const index = updatedList.findIndex(
            (issue) => issue.no_issue === newIssue.no_issue
          );
          if (index !== -1) {
            updatedList[index] = newIssue;
          }
        });

        return updatedList;
      });

      setPage(data[0][0]);
      setSelectedIssue(data[0][0]);
    }
  };

  const sendMessage = async (data: any) => {
    // 링크 생성은 우선순위: 선택된 이슈 -> 전달된 data -> 폼데이터
    const params = {
      ...formData,
      ...data,
      ...{ cd_firm: userInfo.cd_firm, msg_flag: "reply" },
    };
    // 2026-03-09: const result = callApi("telegram/sendMsg", params);
    await sendIssueSnapshotTelegramByParams(params);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const onMenuBtnsClick = async (id: string) => {
    if (!selectedIssue) return;
    const st_issue = selectedIssue.st_issue;
    let result = false;

    if (id === "btnReject") {
      if (st_issue === "4" || st_issue === "T") {
        // Done 또는 Test -> solving
        selectedIssue.st_issue = "3";
        selectedIssue.tm_reject = getCurrentTime();
        selectedIssue.cd_emp_reject = userInfo.cd_emp;
      } else {
        await showDialog("alter", "Done, Test 상태에서만 Reject 가능합니다.");
        return;
      }

      setSelectedIssue(selectedIssue);
      result = await onSave(selectedIssue);
      await openReplyPopup(selectedIssue);
    } else if (id === "btnConfirm") {
      const previousStIssue = st_issue;
      if (st_issue === "4" || st_issue === "T") {
        selectedIssue.st_issue = "5";
        selectedIssue.tm_confirm = getCurrentTime();
      } else {
        await showDialog("alter", "Done, Test 상태에서만 Confirm 가능합니다.");
        return;
      }

      setSelectedIssue(selectedIssue);
      result = await saveIssueStatusAndAcceptDeployQueue(
        selectedIssue,
        "CONF",
        previousStIssue
      );
    } else if (id === "btnCancel") {
      selectedIssue.st_issue = "9";
      selectedIssue.tm_cancel = getCurrentTime();
      selectedIssue.cd_emp_cancel = userInfo.cd_emp;
      setSelectedIssue(selectedIssue);
      result = await onSave(selectedIssue);
      await openReplyPopup(selectedIssue);
    } else if (id === "btnDelete") {
      if (
        userInfo.cd_emp === selectedIssue.cd_emp ||
        userInfo.cd_emp === selectedIssue.cd_emp_admin
      ) {
        if (
          st_issue === "0" ||
          st_issue === "1" ||
          st_issue === "2" ||
          st_issue === "9"
        ) {
          await onDelete();
        } else {
          await showDialog(
            "alter",
            "Temp, Open, Assign, Cancel 상태에서만 삭제 가능합니다."
          );
        }
      } else {
        await showDialog("alter", "등록자만 삭제 가능합니다.");
      }
    } else if (id === "btnUpdate") {
      // popup 띄우기
      const component = (
        <SN_SFA_ISSUE_REP_005_POP_002
          selectedData={selectedIssue}
          Menuprops={Menuprops}
        />
      );
      const result = await showIssueModal({
        title: "이슈수정 도움창",
        children: component,
        width: "75%",
        height: "60%",
      });
      if (result) {
        try {
          // cd_emp와 nm_emp_e 조회
          let form = searchRef.current.getFormdata();
          const searchEmp = form.search_emp?.trim() || "";

          // 1. 한글 포함 여부 체크
          const isKorean = /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(searchEmp);

          // 2. 검색 조건 설정
          const empSearchParam = {
            cd_firm: form.cd_firm,
            ...(isKorean ? { nm_emp: searchEmp } : { nm_emp_e: searchEmp }),
          };

          // 3. 조회 요청
          const params1 = {
            id: "sn_sfa_issue_rep_005_03",
            params: empSearchParam,
          };
          const empData: any = await fnGetDataSet(params1);

          const cd_emp =
            empData?.[0]?.map((item: any) => item.cd_emp).join(",") || "";
          const nm_emp =
            empData?.[0]?.map((item: any) => item.nm_emp).join(",") ||
            searchEmp;

          const params2 = {
            id: "sn_sfa_issue_rep_005_04",
            params: {
              no_issue: selectedIssue.no_issue,
            },
          };
          const data: any = await fnGetDataSet(params2);

          if (data[0] && data[0].length > 0) {
            setIssueList((prevIssueList: any) => {
              const newIssues = data[0];

              // 기존 리스트를 복사
              let updatedList = [...prevIssueList];
              newIssues.forEach((newIssue: any) => {
                const index = updatedList.findIndex(
                  (issue) => issue.no_issue === newIssue.no_issue
                );
                if (index !== -1) {
                  // 같은 no_issue가 있으면 덮어쓰기
                  updatedList[index] = newIssue;
                }
              });

              return updatedList;
            });
            setPage(data[0][0]);
            setSelectedIssue(data[0][0]);
            setEditorBody("");
          }
        } catch (error: any) {}
      }
    } else if (id === "btnZoomIn") {
      // 확대보기
      searchRef.current.handleExpand(false);

      const prevSize = {
        panel1width: sizes.panel1width
          ? sizes.panel1width
          : defaultSize.panel1width,
        panel2width: sizes.panel2width
          ? sizes.panel2width
          : defaultSize.panel2width,
        panel3height: sizes.panel3height
          ? sizes.panel3height
          : defaultSize.panel3height,
      };

      if (
        sizes.panel1width !== "9.5%" &&
        sizes.panel2width !== "90%" &&
        sizes.panel3height !== "200"
      ) {
        zoomRef.current = prevSize;
      }

      sizes.panel1width = "9.5%";
      sizes.panel2width = "90%";
      sizes.panel3height = "200";
    } else if (id === "btnZoomOut") {
      // 축소보기
      searchRef.current.handleExpand(true);
      const prevSize = zoomRef.current;
      if (prevSize) {
        sizes.panel1width = prevSize.panel1width;
        sizes.panel2width = prevSize.panel2width;
        sizes.panel3height = prevSize.panel3height;
      } else {
        sizes.panel1width = defaultSize.panel1width;
        sizes.panel2width = defaultSize.panel2width;
        sizes.panel3height = defaultSize.panel3height;
      }
    } else if (id === "btnResetSize") {
      // 사이즈 초기화
      if (!onResizeStop) return;
      Object.entries(defaultSize).forEach(([key, value]) => {
        const match = key.match(/\d+/);
        if (match) {
          const seq = Number(match[0]);
          onResizeStop(seq, value, { skipStorage: true });
        }
      });
      const cd_menu = "SN_SFA_ISSUE_REP_005";
      if (!cd_menu) return;
      const menuSize = localStorage.getItem("menuSize");
      if (!menuSize) return;
      const parsed = JSON.parse(menuSize);
      delete parsed[cd_menu];
      localStorage.setItem("menuSize", JSON.stringify(parsed));
    }

    if (result) {
      await showDialog("success", "이슈 상태가 변경되었습니다.");
    }
  };

  const handleHeaderSizeToggle = () => {
    if (!isExpandedHeader) {
      setIsExpandedHeader(true);
    } else {
      setIsExpandedHeader(false);
    }
  };

  const downloadFile = async (e: any) => {
    const target = e.target as HTMLElement;

    if (target.closest(".file-widget-download")) {
      const widgetEl = target.closest(".file-widget") as HTMLElement;
      const fileUrl = widgetEl.dataset.fileUrl;
      const fileName = widgetEl.dataset.fileName;

      if (!fileUrl) return;

      try {
        const params = {
          dc_url: fileUrl,
        };
        const res = await fetch("/snotes/file/download2", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || 'DEFAULT'
          },
          body: JSON.stringify(params),
        });
        if (!res.ok) throw new Error("다운로드 실패");

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "download";
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        alert("파일 다운로드 실패");
      }
    }
  };

  const openReplyPopup = async (selectedIssue: any) => {
    if (!selectedIssue) return;

    const component = (
      <SN_SFA_ISSUE_REG_005_POP_002
        formData={selectedIssue}
        replyList={replyList}
      />
    );
    const result = await showIssueModal({
      title: "Reply",
      children: component,
    });
    if (result) {
      await onSearch();
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          
        ></SearchPanel>
      </div>
      <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={50}
        >
          <div
            className="w-full h-full"
            style={{
              border: "1px solid #dee2e6",
              boxSizing: "border-box",
              backgroundColor: "white",
              overflowX: "auto",
              overflowY: "auto",
            }}
          >
            <List
              sx={{
                width: "max-content",
                minWidth: "100%",
                height: "100%",
                paddingTop: 0,
              }}
            >
              {Object.entries(
                issueList.reduce(
                  (acc: any, cur: any) => {
                    const dateKey = cur.dt_issue;
                    if (!acc[dateKey]) acc[dateKey] = [];
                    acc[dateKey].push(cur);
                    return acc;
                  },
                  {} as Record<string, any[]>
                )
              ).map(([date, issues]) => (
                <div key={date}>
                  {/* 날짜 헤더 */}
                  <div
                    style={{
                      backgroundColor: "rgb(163 204 163)",
                      padding: "4px 8px",
                      fontSize: "24px",
                      minWidth: "800px",
                    }}
                  >
                    {date}
                  </div>

                  {/* 이슈 리스트 */}
                  {(issues as any[]).map((issue: any, idx: number) => (
                    <div
                      className={`issue-row ${
                        selectedIssue.no_issue === issue.no_issue
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => {
                        setPage(issue);
                        setSelectedIssue(issue);
                        setEditorBody("");
                      }}
                      onDoubleClick={() =>
                        handleDoubleClick({
                          ...issue,
                          ...{ detailBody: detailBody },
                        })
                      }
                      style={{
                        display: "flex",
                        minWidth: "800px",
                        border: "1px solid #ccc",
                        padding: "12px",
                        gap: "12px",
                        cursor: "pointer",
                      }}
                    >
                      {/* 아이콘 영역 */}
                      <div
                        style={{
                          width: "35px",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src="/images/login_image.webp"
                          style={{ width: "30px", height: "30px" }}
                        />
                      </div>

                      {/* 왼쪽 정보: fg_item + ty_issue1 */}
                      <div
                        style={{
                          minWidth: "110px",
                          flexShrink: 0,
                          fontSize: "14px",
                        }}
                      >
                        <div style={{ marginBottom: "12px" }}>
                          {issue.nm_fg_item}
                        </div>
                        <div>{issue.nm_tp_issue1}</div>
                      </div>

                      {/* 중앙 영역: 상태, 이슈번호, 제목, 수신자 */}
                      <div
                        style={{ flex: 1, minWidth: "350px", flexShrink: 0 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                            fontSize: "14px",
                          }}
                        >
                          <div style={{ width: "60px", color: "red" }}>
                            {STATUS_MAP[issue.st_issue] || ""}
                          </div>
                          <div style={{ width: "110px" }}>{issue.no_issue}</div>
                          <div style={{}}>등록자: {issue.nm_emp}</div>
                        </div>
                        <div
                          style={{
                            width: "100%",
                            color: "#1976d2",
                            fontWeight: "bold",
                            fontSize: "16px",
                            marginTop: "4px",
                          }}
                        >
                          {issue.dc_title?.trim() +
                            (issue.reply_length && issue.reply_length !== 0
                              ? " [" + issue.reply_length + "]"
                              : "")}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            marginTop: "4px",
                          }}
                        >
                          <div>담당자 : {issue.nm_recipient}</div>
                          <div>참조자 : {issue.nm_reference}</div>
                        </div>
                      </div>

                      {/* 오른쪽 정보: 날짜 등 */}
                      <div
                        style={{
                          minWidth: "170px",
                          fontSize: "14px",
                          lineHeight: "1.6",
                          textAlign: "left",
                          alignSelf: "flex-end",
                        }}
                      >
                        <div>관리자: {issue.nm_emp_admin}</div>
                        <div>등록일: {issue.dt_issue}</div>
                        <div>마감일: {issue.dt_due}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </List>
          </div>
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={50}
        >
          <div
            className="w-full h-full"
            style={{
              border: "1px solid #dee2e6",
              boxSizing: "border-box",
              backgroundColor: "white",
              display: "flex",
              flexDirection: "column", // 추가
              height: "100%", // 추가
            }}
          >
            {/* issue 상세 내용 */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              {issueList && issueList.length > 0 ? (
                <>
                  <div
                    style={{
                      backgroundColor: "#ebebeb",
                      padding: "6px 8px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      borderBottom: "1px solid #ccc",
                      height: isExpandedHeader ? "auto" : "40px",
                      overflow: isExpandedHeader ? "visible" : "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      cursor: "pointer",
                      alignItems: isExpandedHeader ? "flex-start" : "center",
                      gap: "8px",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <IconButton
                      color="inherit"
                      aria-controls={open ? "menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? "true" : undefined}
                      onClick={handleOpenMenu}
                    >
                      <MoreIcon fontSize="small" />
                    </IconButton>
                    <div
                      title={toolTipTitle}
                      style={{
                        display: "inline-block",
                        width: "100%",
                        height: isExpandedHeader ? "auto" : "40px",
                        overflow: isExpandedHeader ? "visible" : "hidden",
                        textOverflow: isExpandedHeader ? "unset" : "ellipsis",
                        whiteSpace: isExpandedHeader ? "normal" : "nowrap",
                        lineHeight: isExpandedHeader ? "normal" : "40px",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: detailTitle ?? "",
                      }}
                      onClick={handleHeaderSizeToggle}
                    />
                  </div>

                  <div
                    style={{
                      padding: "16px",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #eee",
                      fontSize: "14px",
                      lineHeight: "1.6",
                    }}
                  >
                    <div style={{ display: "flex", gap: "6px", flex: 1 }}>
                      <div style={{ fontWeight: "bold" }}>•</div>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: detailBody ? detailBody.txt_body : "",
                        }}
                        onClick={downloadFile}
                      ></div>
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "12px",
                        marginTop: "8px",
                      }}
                    >
                      {detailBody ? detailBody.tm_reg : ""}
                    </div>
                  </div>

                  <div>
                    {Array.isArray(replyList) &&
                      replyList.map((reply, idx) =>
                        reply && reply.txt_body ? (
                          <div
                            key={idx}
                            style={{
                              padding: "16px",
                              borderBottom: "1px solid #eee",
                              fontSize: "14px",
                              lineHeight: "1.6",
                            }}
                          >
                            <div
                              style={{
                                marginBottom: "4px",
                              }}
                            >
                              • {reply.nm_emp ? reply.nm_emp : ""} &gt;
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                paddingLeft: "8px",
                              }}
                              dangerouslySetInnerHTML={{
                                __html: reply ? reply.txt_body : "",
                              }}
                              onClick={downloadFile}
                            />
                            <div
                              style={{
                                textAlign: "right",
                                fontSize: "12px",
                                marginTop: "8px",
                              }}
                            >
                              {reply.tm_reg}
                            </div>
                          </div>
                        ) : null
                      )}
                  </div>
                </>
              ) : null}
            </div>
            {selectedIssue?.no_issue && (
              <SnResizable
                direction={"height"}
                seq={3}
                cd_menu={cd_menu}
                defaultSize={defaultSize}
                size={sizes.panel3height}
                onResizeStop={onResizeStop}
                enable={{ top: true }}
                minHeight={50}
              >
                <div
                  style={{
                    height: "100%",
                    flexShrink: 0,
                    borderTop: "1px solid rgb(215 205 205)",
                    background: "#fafafa",
                  }}
                >
                  <SnEditor
                    uniqueId={uniqueId}
                    selectedData={selectedIssue.no_issue}
                    body={editorBody}
                    setBody={setEditorBody}
                  />
                </div>
              </SnResizable>
            )}

            <Menu
              id="menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseMenu}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
              sx={{
                border: "1px solid #e5e7eb",
                boxShadow: "rgba(186, 191, 199, 0.4) 0px 1px 4px 1px",
                fontSize: "13px",
              }}
            >
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnUpdate");
                  handleCloseMenu();
                }}
              >
                이슈정보수정
              </MenuItem>
              <Divider />
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnZoomIn");
                  handleCloseMenu();
                }}
              >
                확대보기
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnZoomOut");
                  handleCloseMenu();
                }}
              >
                축소보기
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnResetSize");
                  handleCloseMenu();
                }}
              >
                사이즈 초기화
              </MenuItem>
              <Divider />
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnReject");
                  handleCloseMenu();
                }}
              >
                REJECT
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnConfirm");
                  handleCloseMenu();
                }}
              >
                CONFIRM
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnCancel");
                  handleCloseMenu();
                }}
              >
                CANCEL
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "13px" }}
                onClick={() => {
                  onMenuBtnsClick("btnDelete");
                  handleCloseMenu();
                }}
              >
                DELETE
              </MenuItem>
            </Menu>
          </div>
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_SFA_ISSUE_REP_005;
